import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, type InputProps } from "./Input";
import { Eye, EyeOff } from "@/icons";

export interface PasswordInputProps extends Omit<InputProps, "type" | "rightIcon"> {
  /**
   * Override the toggle's aria-label. A field holding something that is not
   * literally a password — the MFA recovery passphrase — should name what it
   * actually holds, so a screen-reader user is not told "show password" about
   * a passphrase. Both labels move together or neither does.
   */
  showLabel?: string;
  hideLabel?: string;
}

// Input preset for passwords: a built-in show/hide eye toggle wired to the
// Input's rightIcon, so a caller just swaps <Input type="password"> →
// <PasswordInput>. Reused by the auth modal, password reset, the settings
// security forms and MFA passphrase enrolment so every secret field reveals
// consistently.
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ showLabel, hideLabel, ...props }, ref) {
    const { t } = useTranslation("ui");
    const [show, setShow] = useState(false);
    return (
      <Input
        ref={ref}
        type={show ? "text" : "password"}
        rightIcon={
          <button
            type="button"
            // Skip the tab order: the field itself is the focus target; the eye
            // is a pointer affordance, not a form step.
            tabIndex={-1}
            onClick={() => setShow((s) => !s)}
            aria-label={show ? (hideLabel ?? t("password.hide")) : (showLabel ?? t("password.show"))}
            className="pointer-events-auto -mr-1 rounded p-0.5 text-fg-muted transition-colors hover:text-fg"
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        }
        {...props}
      />
    );
  },
);
